import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { seoArticles } from '@/mocks/seo-articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SeoArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <title>Article Not Found | VigorBuy Spreadsheet</title>
        <meta name="description" content="The requested article could not be found. Browse our collection of VigorBuy Spreadsheet guides and resources." />
        <Navbar />
        <main className="pt-32 pb-20">
          <div className="max-w-[860px] mx-auto px-6 md:px-10 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-8">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug = slug || '';

  return (
    <>
      <title>{article.title} | VigorBuy Spreadsheet</title>
      <meta name="description" content={article.description} />
      <link rel="canonical" href={`https://vigorbuy.cam/seo/${article.slug}`} />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.h1,
        description: article.description,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Organization', name: 'VigorBuy Spreadsheet' },
        publisher: { '@type': 'Organization', name: 'VigorBuy Spreadsheet' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://vigorbuy.cam/seo/${article.slug}` },
      })}</script>
      <Navbar />
      <main>
        <article className="pt-28 pb-20">
          <div className="max-w-[860px] mx-auto px-6 md:px-10">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                    Home
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li>
                  <Link to="/#seo-articles" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                    Guides
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li className="text-primary-600 font-medium truncate max-w-[200px]">
                  {article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}
                </li>
              </ol>
            </nav>

            <header className="mb-12">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-[11px] font-semibold uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 tracking-tight leading-tight">
                {article.h1}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span>Published: {article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="mb-12 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto"
                title={article.title}
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx} className="mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight mb-5">
                    {section.heading}
                  </h2>
                  {section.content.map((paragraph, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-base text-foreground-700 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: paragraph }}
                    ></p>
                  ))}
                </section>
              ))}
            </div>

            <div className="mt-16 pt-10 border-t border-background-200">
              <div className="bg-background-100 rounded-lg p-8 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                  Ready to Explore More?
                </h3>
                <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                  Continue exploring the latest <strong>VigorBuy Spreadsheet</strong> guides, comparisons, and shopping resources.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                  >
                    <i className="ri-home-4-line text-base"></i>
                    Back to Homepage
                  </Link>
                  <Link
                    to="/vigorbuy-sheet-seo-guide"
                    className="px-6 py-3 rounded-md bg-secondary-100 text-secondary-800 text-sm font-semibold hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                  >
                    <i className="ri-book-open-line text-base"></i>
                    Original SEO Guide
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 rounded-md bg-accent-100 text-accent-700 text-sm font-semibold hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                  >
                    Visit Main Store
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-10 border-t border-background-200">
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">More Guides</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {seoArticles
                  .filter((a) => a.slug !== categorySlug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/seo/${a.slug}`}
                      className="flex items-center gap-3 p-3 rounded-md bg-background-50 hover:bg-background-100 transition-colors duration-200 cursor-pointer group"
                    >
                      <img
                        src={a.imageUrl}
                        alt={a.title}
                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                      />
                      <span className="text-xs text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 leading-tight line-clamp-2">
                        {a.title}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}